import { CommonModule } from '@angular/common';
import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    ChangeDetectionStrategy
} from '@angular/core';
import { NoteView } from '@api-proxies';
import { NoteService } from '@http/clients/note.service';
import { UserRoles } from '@models/enums/UserRoles';
import { AuthService } from '@services/auth.service';
import { BadgeComponent, ModalService } from 'ngx-lib';
import { KebabMenuComponent } from '../../kebab-menu/kebab-menu.component';
import { ModalConfirmComponent } from '../../modals/modal-confirm/modal-confirm.component';
import { ModalEditNoteComponent } from '../../modals/modal-edit-note/modal-edit-note.component';

export interface INoteEdit {
    index: number;
    editedNote?: NoteView;
}

@Component({
    selector: 'app-note-content',
    templateUrl: './note-content.component.html',
    styleUrls: ['./note-content.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [CommonModule, BadgeComponent, KebabMenuComponent]
})
export class NoteContentComponent implements OnChanges {
    @Input()
    noteId?: number;

    @Input()
    index?: number;

    @Input()
    entityName?: string;

    @Input()
    organizationId?: number;

    note?: NoteView;

    @Output()
    noteChanged: EventEmitter<INoteEdit>;

    canEditNote: boolean;

    constructor(
        private readonly noteService: NoteService,
        private readonly modalService: ModalService,
        private readonly authService: AuthService
    ) {
        this.noteChanged = new EventEmitter<INoteEdit>();
        this.canEditNote = false;
    }

    async ngOnChanges(changes: SimpleChanges): Promise<void> {
        if (changes['noteId'] && changes['index'] && this.noteId) {
            this.note = await this.noteService.getById(this.noteId);
            const userHasRestrictedAccess = this.authService.hasRestrictedAccess(UserRoles.Manager);
            this.canEditNote =
                !userHasRestrictedAccess ||
                (userHasRestrictedAccess && this.authService.getUserFromStorage()?.id === this.note?.userId);
        }
    }

    async editNote(index: number) {
        if (this.note) {
            const editedNote = await this.modalService.open(ModalEditNoteComponent, {
                note: this.note,
                entityId: this.note.entityId,
                entityTypeId: this.note.entityTypeId,
                notificationTitle: this.entityName,
                organizationId: this.organizationId
            });

            if (!editedNote) return;

            this.note = editedNote;
            this.noteChanged.emit({
                index,
                editedNote
            });
        }
    }

    async removeNote(id: number, index: number) {
        const responseOk = await this.modalService.open(ModalConfirmComponent, {
            acceptCaption: 'Remove',
            cancelCaption: 'Cancel',
            content: 'Are you sure you want to remove this note?',
            title: 'Remove note'
        });

        if (!responseOk) return;

        await this.noteService.delete(id);
        this.noteChanged.emit({
            index
        });
    }
}
